import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaCog, 
  FaBell, 
  FaShieldAlt, 
  FaPalette, 
  FaLanguage,
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaTimes as FaTimesIcon
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';

// Styled Components
const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.2s ease;
      text-decoration: none;
      
      &:hover {
        color: #F68B21;
        background: rgba(246, 139, 33, 0.1);
      }
    }
  }
  
  h1 {
    font-size: 2rem;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SettingsSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  
  h3 {
    color: #141340;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    
    .icon {
      color: #F68B21;
    }
  }
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f1f1f1;
  
  &:last-child {
    border-bottom: none;
  }
  
  .setting-info {
    flex: 1;
    
    .setting-title {
      font-weight: 600;
      color: #141340;
      margin-bottom: 0.25rem;
    }
    
    .setting-description {
      font-size: 0.9rem;
      color: #6c757d;
    }
  }
  
  .setting-control {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.3s;
    border-radius: 24px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
  
  input:checked + .slider {
    background-color: #F68B21;
  }
  
  input:checked + .slider:before {
    transform: translateX(26px);
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  color: #141340;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 2px rgba(246, 139, 33, 0.1);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    font-weight: 600;
    color: #141340;
    margin-bottom: 0.5rem;
  }
  
  input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #F68B21;
      box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
    }
  }
  
  .input-group {
    position: relative;
    
    .password-toggle {
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      
      &:hover {
        color: #F68B21;
      }
    }
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e9ecef;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  color: #6c757d;
  border: 1px solid #e9ecef;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: #F68B21;
    border-color: #F68B21;
    background: rgba(246, 139, 33, 0.1);
  }
`;

const Alert = styled.div<{ type: 'success' | 'error' }>`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.type === 'success' ? `
    background: rgba(40, 167, 69, 0.1);
    color: #28a745;
    border: 1px solid rgba(40, 167, 69, 0.2);
  ` : `
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
    border: 1px solid rgba(220, 53, 69, 0.2);
  `}
`;

const ManagerSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    fundRequestAlerts: true,
    projectUpdates: true,
    weeklyReports: false,
    monthlyReports: true
  });
  
  // Security settings
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: '30',
    passwordExpiry: '90'
  });
  
  // Appearance settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'light',
    language: 'en',
    fontSize: 'medium'
  });
  
  // Password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const handleNotificationChange = (setting: string, value: boolean) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  
  const handleSecurityChange = (setting: string, value: string) => {
    setSecuritySettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  
  const handleAppearanceChange = (setting: string, value: string) => {
    setAppearanceSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  
  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  const validatePasswordChange = () => {
    if (!passwordData.currentPassword) {
      setAlert({ type: 'error', message: 'Current password is required' });
      return false;
    }
    if (!passwordData.newPassword) {
      setAlert({ type: 'error', message: 'New password is required' });
      return false;
    }
    if (passwordData.newPassword.length < 8) {
      setAlert({ type: 'error', message: 'New password must be at least 8 characters' });
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setAlert({ type: 'error', message: 'New passwords do not match' });
      return false;
    }
    return true;
  };
  
  const handlePasswordSave = async () => {
    if (!validatePasswordChange()) return;
    
    setSaving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setAlert({ type: 'success', message: 'Password updated successfully' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to update password' });
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveAll = async () => {
    setSaving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setAlert({ type: 'success', message: 'Settings saved successfully' });
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/manager')}>
            <IconWrapper icon={FaArrowLeft} />
            Back to Dashboard
          </button>
          <h1>
            <IconWrapper icon={FaCog} className="icon" />
            Settings
          </h1>
        </div>
      </PageHeader>
      
      {alert && (
        <Alert type={alert.type}>
          <IconWrapper icon={alert.type === 'success' ? FaCheck : FaTimesIcon} />
          {alert.message}
        </Alert>
      )}
      
      <SettingsContainer>
        <SettingsSection>
          <h3>
            <IconWrapper icon={FaBell} className="icon" />
            Notification Settings
          </h3>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Email Notifications</div>
              <div className="setting-description">Receive notifications via email</div>
            </div>
            <div className="setting-control">
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={notificationSettings.emailNotifications}
                  onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                />
                <span className="slider"></span>
              </ToggleSwitch>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Push Notifications</div>
              <div className="setting-description">Receive browser push notifications</div>
            </div>
            <div className="setting-control">
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={notificationSettings.pushNotifications}
                  onChange={(e) => handleNotificationChange('pushNotifications', e.target.checked)}
                />
                <span className="slider"></span>
              </ToggleSwitch>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Fund Request Alerts</div>
              <div className="setting-description">Get notified when new fund requests are submitted</div>
            </div>
            <div className="setting-control">
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={notificationSettings.fundRequestAlerts}
                  onChange={(e) => handleNotificationChange('fundRequestAlerts', e.target.checked)}
                />
                <span className="slider"></span>
              </ToggleSwitch>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Project Updates</div>
              <div className="setting-description">Receive updates about project progress</div>
            </div>
            <div className="setting-control">
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={notificationSettings.projectUpdates}
                  onChange={(e) => handleNotificationChange('projectUpdates', e.target.checked)}
                />
                <span className="slider"></span>
              </ToggleSwitch>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Weekly Reports</div>
              <div className="setting-description">Receive weekly project reports</div>
            </div>
            <div className="setting-control">
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={notificationSettings.weeklyReports}
                  onChange={(e) => handleNotificationChange('weeklyReports', e.target.checked)}
                />
                <span className="slider"></span>
              </ToggleSwitch>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Monthly Reports</div>
              <div className="setting-description">Receive monthly project reports</div>
            </div>
            <div className="setting-control">
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={notificationSettings.monthlyReports}
                  onChange={(e) => handleNotificationChange('monthlyReports', e.target.checked)}
                />
                <span className="slider"></span>
              </ToggleSwitch>
            </div>
          </SettingItem>
        </SettingsSection>
        
        <SettingsSection>
          <h3>
            <IconWrapper icon={FaShieldAlt} className="icon" />
            Security Settings
          </h3>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Two-Factor Authentication</div>
              <div className="setting-description">Add an extra layer of security to your account</div>
            </div>
            <div className="setting-control">
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={securitySettings.twoFactorAuth}
                  onChange={(e) => handleSecurityChange('twoFactorAuth', e.target.checked.toString())}
                />
                <span className="slider"></span>
              </ToggleSwitch>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Session Timeout</div>
              <div className="setting-description">Automatically log out after inactivity</div>
            </div>
            <div className="setting-control">
              <Select
                value={securitySettings.sessionTimeout}
                onChange={(e) => handleSecurityChange('sessionTimeout', e.target.value)}
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
              </Select>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Password Expiry</div>
              <div className="setting-description">Days before password expires</div>
            </div>
            <div className="setting-control">
              <Select
                value={securitySettings.passwordExpiry}
                onChange={(e) => handleSecurityChange('passwordExpiry', e.target.value)}
              >
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
              </Select>
            </div>
          </SettingItem>
        </SettingsSection>
        
        <SettingsSection>
          <h3>
            <IconWrapper icon={FaPalette} className="icon" />
            Appearance Settings
          </h3>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Theme</div>
              <div className="setting-description">Choose your preferred theme</div>
            </div>
            <div className="setting-control">
              <Select
                value={appearanceSettings.theme}
                onChange={(e) => handleAppearanceChange('theme', e.target.value)}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto</option>
              </Select>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Language</div>
              <div className="setting-description">Choose your preferred language</div>
            </div>
            <div className="setting-control">
              <Select
                value={appearanceSettings.language}
                onChange={(e) => handleAppearanceChange('language', e.target.value)}
              >
                <option value="en">English</option>
                <option value="sw">Swahili</option>
              </Select>
            </div>
          </SettingItem>
          
          <SettingItem>
            <div className="setting-info">
              <div className="setting-title">Font Size</div>
              <div className="setting-description">Adjust the text size</div>
            </div>
            <div className="setting-control">
              <Select
                value={appearanceSettings.fontSize}
                onChange={(e) => handleAppearanceChange('fontSize', e.target.value)}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </Select>
            </div>
          </SettingItem>
        </SettingsSection>
        
        <SettingsSection>
          <h3>
            <IconWrapper icon={FaShieldAlt} className="icon" />
            Change Password
          </h3>
          
          <FormGroup>
            <label>Current Password</label>
            <div className="input-group">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                placeholder="Enter current password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('current')}
              >
                <IconWrapper icon={showPasswords.current ? FaEyeSlash : FaEye} />
              </button>
            </div>
          </FormGroup>
          
          <FormGroup>
            <label>New Password</label>
            <div className="input-group">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="Enter new password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('new')}
              >
                <IconWrapper icon={showPasswords.new ? FaEyeSlash : FaEye} />
              </button>
            </div>
          </FormGroup>
          
          <FormGroup>
            <label>Confirm New Password</label>
            <div className="input-group">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                <IconWrapper icon={showPasswords.confirm ? FaEyeSlash : FaEye} />
              </button>
            </div>
          </FormGroup>
          
          <FormActions>
            <SaveButton onClick={handlePasswordSave} disabled={saving}>
              <IconWrapper icon={FaSave} />
              {saving ? 'Updating...' : 'Update Password'}
            </SaveButton>
          </FormActions>
        </SettingsSection>
        
        <FormActions>
          <CancelButton onClick={() => navigate('/manager')}>
            <IconWrapper icon={FaTimes} />
            Cancel
          </CancelButton>
          <SaveButton onClick={handleSaveAll} disabled={saving}>
            <IconWrapper icon={FaSave} />
            {saving ? 'Saving...' : 'Save All Settings'}
          </SaveButton>
        </FormActions>
      </SettingsContainer>
    </PageContainer>
  );
};

export default ManagerSettings; 